import { useState, type FormEvent } from 'react'
import { Box, Button, Group, Text, TextInput } from '@mantine/core'
import { HUBSPOT_PORTAL_ID, HUBSPOT_SUBSCRIBE_FORM_ID } from './HubSpotForm'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Status = 'idle' | 'submitting' | 'success' | 'error'

/** Reads the HubSpot visitor tracking cookie, if the tracking script has set one. */
const readHubSpotCookie = (): string | undefined =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith('hubspotutk='))
    ?.split('=')[1]

export interface SubscribeFormProps {
  /** HubSpot form GUID. Defaults to the subscribe form. */
  formId?: string
  /** HubSpot portal ID. */
  portalId?: string
  /** Submit button label. */
  buttonLabel?: string
  /** 'light' = white button for use on a dark background. */
  buttonVariant?: 'primary' | 'light'
}

/**
 * Native email capture posting directly to the HubSpot Forms Submission API.
 *
 * Replaces the old iframe embed (js.hsforms.net): no third-party script, no
 * cross-origin iframe, no third-party cookies, and the markup is ours to style.
 * The submission endpoint is public and CORS-enabled — it takes no API key, so
 * nothing secret is exposed to the browser.
 */
export const SubscribeForm = ({
  formId = HUBSPOT_SUBSCRIBE_FORM_ID,
  portalId = HUBSPOT_PORTAL_ID,
  buttonLabel = 'Get Free ETF insights',
  buttonVariant = 'primary'
}: SubscribeFormProps) => {
  const [email, setEmail] = useState('')
  // Honeypot: hidden from real users, but bots that auto-fill every field
  // will populate it. A non-empty value silently drops the submission.
  const [honeypot, setHoneypot] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState('')

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // Bot trap: pretend success so the bot moves on, but send nothing.
    if (honeypot) {
      setStatus('success')
      setMessage('Thanks for subscribing. Check your inbox to confirm.')
      setEmail('')
      return
    }

    if (!EMAIL_PATTERN.test(email.trim())) {
      setStatus('error')
      setMessage('Please enter a valid email address.')
      return
    }

    setStatus('submitting')
    setMessage('')

    const hutk = readHubSpotCookie()

    try {
      const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: [{ objectTypeId: '0-1', name: 'email', value: email.trim() }],
          context: {
            ...(hutk ? { hutk } : {}),
            pageUri: window.location.href,
            pageName: document.title
          }
        })
      })

      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(body?.errors?.[0]?.message || 'Submission failed')
      }

      setStatus('success')
      setMessage('Thanks for subscribing. Check your inbox to confirm.')
      setEmail('')
    } catch (error) {
      console.error('HubSpot subscribe failed:', error)
      setStatus('error')
      setMessage('Something went wrong. Please try again.')
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} w="100%" noValidate>
      {/* Honeypot — visually hidden and skipped by keyboard/screen readers.
          Only automated bots that fill every field will populate it. */}
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(event) => setHoneypot(event.currentTarget.value)}
        style={{
          position: 'absolute',
          left: '-9999px',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none'
        }}
      />
      <Group gap="sm" align="flex-start" wrap="nowrap">
        <TextInput
          type="email"
          name="email"
          value={email}
          onChange={(event) => {
            setEmail(event.currentTarget.value)
            if (status === 'error') {
              setStatus('idle')
              setMessage('')
            }
          }}
          placeholder="Enter your email"
          aria-label="Email address"
          aria-invalid={status === 'error'}
          disabled={status === 'submitting'}
          size="md"
          radius="xs"
          style={{ flex: 1, minWidth: 0 }}
          styles={
            buttonVariant === 'light'
              ? {
                  input: {
                    backgroundColor: 'transparent',
                    color: '#BFDFFF',
                    borderColor: '#BFDFFF',
                    '&::placeholder': { color: '#BFDFFF', opacity: 0.7 }
                  }
                }
              : undefined
          }
        />
        <Button
          type="submit"
          size="md"
          radius="xs"
          px="md"
          loading={status === 'submitting'}
          style={{ flexShrink: 0 }}
          styles={
            buttonVariant === 'light'
              ? { root: { backgroundColor: '#fff', color: '#054484' } }
              : undefined
          }>
          {buttonLabel}
        </Button>
      </Group>

      {message && (
        <Text mt="2xs" fz="sm" c={status === 'error' ? 'red.4' : 'green.4'} role={status === 'error' ? 'alert' : 'status'}>
          {message}
        </Text>
      )}
    </Box>
  )
}

export default SubscribeForm
